/**
 * 食品のコマンドパレット
 *
 * 検索欄 1 つでお気に入り・履歴から選ぶ。空欄なら最近食べたものとよく使うお気に入りを出す。
 * DB（kalori.jp）は遅いのでここでは検索せず、「DB で検索」で DB タブに入力した文字を渡す。
 * 選んだときの動き（カートに入れる / すぐ記録する）は onPick で呼び出し側が決める。
 */

import { Check, Database, History, Loader2, Pencil, Sparkles, Star } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import type { FoodTemplate, MealItemHistoryEntry } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

import type { PalettePick } from '../cart-utils'
import { HistoryEntryLastEaten, HistoryEntryName } from '../history-entry-parts'
import { FOOD_LISTS } from './food-lists'
import type { FoodListKind } from './palette-utils'
import { useFoodPalette } from './use-food-palette'

type Props = {
  templates: FoodTemplate[]
  isSelected: (pick: PalettePick) => boolean
  onPick: (pick: PalettePick) => void
  /** 入力した文字を AI に回す */
  onAiText: (text: string) => void
  /** 入力した文字で DB を検索する（DB タブで検索。DB は遅いのでパレットでは検索しない） */
  onSearchDb: (query: string) => void
  aiLabel?: (text: string) => string
  onEditTemplate?: (template: FoodTemplate) => void
  /** お気に入り・履歴・DB の一覧を開く（一覧から選びたいとき用） */
  onOpenList?: (kind: FoodListKind) => void
  /** 検索欄の左に置くもの（種別の選択など） */
  leading?: ReactNode
  /** 空欄のときの候補を横に並ぶチップで出す（ページに置くとき用） */
  chipsWhenEmpty?: boolean
  placeholder?: string
  disabled?: boolean
  /** 検索欄の行をスクロールしても上に貼り付ける（一覧を外側のスクロールに任せるとき用） */
  stickyInput?: boolean
  /** 一覧のクラス。外側でスクロールさせるなら高さの上限を外す */
  listClassName?: string
  autoFocus?: boolean
  /** 候補とチップを入力欄の上に出す（画面の下に置くとき用） */
  dropUp?: boolean
  /** いちばん外側に足すクラス */
  className?: string
}

function SelectedMark({ selected }: { selected: boolean }) {
  return <Check className={`size-3.5 text-primary ${selected ? '' : 'invisible'}`} />
}

function Kcal({ value }: { value: number }) {
  return <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{Math.round(value)}kcal</span>
}

const defaultAiLabel = (text: string) => `「${text}」を AI で解析して追加`

export function FoodPalette({
  templates,
  isSelected,
  onPick,
  onAiText,
  onSearchDb,
  aiLabel = defaultAiLabel,
  onEditTemplate,
  onOpenList,
  leading,
  chipsWhenEmpty = false,
  placeholder = '食べたものを検索…',
  disabled = false,
  stickyInput = false,
  listClassName = 'max-h-72',
  autoFocus = false,
  dropUp = false,
  className,
}: Props) {
  const [query, setQuery] = useState('')
  const palette = useFoodPalette(templates, query)
  const trimmed = query.trim()

  const historyPick = (entry: MealItemHistoryEntry): PalettePick => ({ kind: 'history', entry })
  const templatePick = (template: FoodTemplate): PalettePick => ({ kind: 'template', template })

  const favoriteRows = palette.favorites.map((t) => (
    <CommandItem key={`fav-${t.id}`} value={`fav-${t.id}`} disabled={disabled} onSelect={() => onPick(templatePick(t))}>
      <SelectedMark selected={isSelected(templatePick(t))} />
      <Star className="size-3.5 text-amber-400" />
      <span className="min-w-0 flex-1 truncate">
        {t.name}
        {t.items.length > 1 && <span className="ml-1 text-[10px] text-muted-foreground">{t.items.length}品</span>}
      </span>
      <Kcal value={t.total_calories} />
      {onEditTemplate && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 text-muted-foreground"
          title="お気に入りを編集"
          aria-label="お気に入りを編集"
          onClick={(e) => {
            e.stopPropagation()
            onEditTemplate(t)
          }}
        >
          <Pencil className="size-3" />
        </Button>
      )}
    </CommandItem>
  ))

  const historyRows = palette.history.map((entry) => (
    <CommandItem
      key={`hist-${entry.item_id}`}
      value={`hist-${entry.item_id}`}
      disabled={disabled}
      onSelect={() => onPick(historyPick(entry))}
    >
      <SelectedMark selected={isSelected(historyPick(entry))} />
      <History className="size-3.5 text-sky-400" />
      <HistoryEntryName entry={entry} className="min-w-0 flex-1" />
      <HistoryEntryLastEaten entry={entry} className="text-[10px]" />
      <Kcal value={entry.calories} />
    </CommandItem>
  ))

  const chips = [
    ...palette.history.map((entry) => ({
      key: `hist-${entry.item_id}`,
      icon: <History className="size-3 text-sky-400" />,
      label: <HistoryEntryName entry={entry} className="max-w-40" />,
      pick: historyPick(entry),
    })),
    ...palette.favorites.map((t) => ({
      key: `fav-${t.id}`,
      icon: <Star className="size-3 text-amber-400" />,
      label: <span className="max-w-32 truncate">{t.name}</span>,
      pick: templatePick(t),
    })),
  ]
  const showChips = chipsWhenEmpty && palette.isEmpty

  return (
    <Command
      shouldFilter={false}
      // sticky は overflow-hidden の親の中では効かないので、貼り付けるときは外す
      className={cn(
        'rounded-md border bg-transparent',
        stickyInput && 'overflow-visible',
        // 入力欄を下にし、候補とチップをその上に出す
        dropUp && 'flex-col-reverse',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1 p-2',
          // 下に置くときは入力欄の塗りと枠で境目が分かるので線は引かない。
          // 上に貼り付けるときは候補が下をくぐってスクロールするので、境目に線を引く
          !dropUp && 'border-b',
          stickyInput && 'sticky top-0 z-10 rounded-t-md bg-background',
        )}
      >
        {leading}
        {/* 背景と同じ色だと入力欄の位置が分からないので、塗りと枠で一目で分かる形にする。
            一覧を開くボタンも枠の中に収めて、入力欄の幅を確保する */}
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center rounded-full pr-1 ring-1 ring-inset ring-foreground/25 transition-shadow focus-within:ring-2 focus-within:ring-primary/80',
            // 下のバーは一段明るい塗りなので、入力欄は暗くくぼませる。シートの中ではページと同じ背景なので明るく浮かせる
            dropUp ? 'bg-background' : 'bg-muted',
          )}
        >
          <div className="min-w-0 flex-1">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={placeholder}
              wrapperClassName="h-10 border-b-0 pl-3 pr-1"
              autoFocus={autoFocus}
              onKeyDown={(e) => {
                if (e.key !== 'Escape' || !query) return
                e.preventDefault()
                e.stopPropagation()
                setQuery('')
              }}
            />
          </div>
          {onOpenList &&
            FOOD_LISTS.map(({ kind, label, Icon, color }) => (
              <Button
                key={kind}
                type="button"
                variant="ghost"
                size="icon"
                className={cn('size-8 shrink-0 rounded-full', color)}
                title={label}
                aria-label={label}
                onClick={() => onOpenList(kind)}
              >
                <Icon className="size-4" />
              </Button>
            ))}
        </div>
      </div>

      {showChips ? (
        <div
          className={cn(
            'flex gap-1.5 overflow-x-auto [scrollbar-width:none]',
            // 入力欄の上に出すときは入力欄に寄せ、上（バーの角丸）との間を空ける
            dropUp ? 'px-2 pt-3' : 'p-2',
          )}
        >
          {palette.historyLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          {!palette.historyLoading && chips.length === 0 && (
            <p className="text-xs text-muted-foreground">食べたものを入力して検索できます</p>
          )}
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              disabled={disabled}
              onClick={() => onPick(chip.pick)}
              className="flex shrink-0 items-center gap-1 rounded-full border border-foreground/15 bg-background px-2.5! py-1! text-xs transition-colors hover:bg-accent disabled:opacity-50"
            >
              {chip.icon}
              {chip.label}
            </button>
          ))}
        </div>
      ) : (
        <CommandList className={listClassName}>
          <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
            {palette.historyLoading ? (
              <Loader2 className="mx-auto size-4 animate-spin" />
            ) : (
              '食べたものを入力して検索できます'
            )}
          </CommandEmpty>

          {palette.isEmpty ? (
            <>
              {historyRows.length > 0 && <CommandGroup heading="最近食べたもの">{historyRows}</CommandGroup>}
              {favoriteRows.length > 0 && <CommandGroup heading="よく使うお気に入り">{favoriteRows}</CommandGroup>}
            </>
          ) : (
            <>
              {favoriteRows.length > 0 && <CommandGroup heading="お気に入り">{favoriteRows}</CommandGroup>}
              {historyRows.length > 0 && <CommandGroup heading="履歴">{historyRows}</CommandGroup>}
            </>
          )}

          {trimmed && (
            <>
              <CommandSeparator />
              <CommandGroup>
                {palette.canSearchDb && (
                  <CommandItem value="action-db" onSelect={() => onSearchDb(trimmed)}>
                    <Database className="size-3.5 text-emerald-400" />
                    <span className="min-w-0 flex-1 truncate">DB で「{trimmed}」を検索</span>
                  </CommandItem>
                )}
                <CommandItem
                  value="action-ai"
                  onSelect={() => {
                    onAiText(trimmed)
                    setQuery('')
                  }}
                >
                  <Sparkles className="size-3.5 text-violet-400" />
                  <span className="min-w-0 flex-1 truncate">{aiLabel(trimmed)}</span>
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </CommandList>
      )}
    </Command>
  )
}
